import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import CountryFlag from 'react-country-flag';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { registerSupplierAction } from '../../../reduxstore/actions/authActions';
import countryCodesData from '../../../../data/countryCodes.json';
import PhoneNumber from 'google-libphonenumber';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Plus, ShieldAlert, X } from 'lucide-react';
import createApiClient from '@/api/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { fetchExistingDocuments } from '@/js/components/FileUploadBox';
import FileUpload from '@/js/components/FileUpload';

const allIncotermOptions = [
  'EXW',
  'FCA',
  'FAS',
  'FOB',
  'CFR',
  'CIF',
  'CPT',
  'CIP',
  'DAP',
  'DPU',
  'DDP',
];

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 5MB
const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
];

const DOCUMENT_TITLES = {
  pan_number_proof: 'PAN NUMBER PROOF',
  gst_number_proof: 'GST NUMBER PROOF',
  udyam_number_proof: 'UDYAM NUMBER PROOF',
  import_export_proof: 'IMPORT/EXPORT CODE PROOF',
  factory_license_proof: 'FACTORY LICENSE PROOF',
};

const formSchema = z.object({
  type_of_organisation: z.string().min(1, 'Organization type is required.'),
  establishment_year: z.string().min(1, 'Establishment year is required.'),
  nature_of_business: z.string().min(1, 'Nature of business is required.'),
  software_used: z.array(z.string()).optional(),
  service_offered: z.array(z.string()).optional(),
  no_of_employee: z.number().min(1, 'Number of employees is required.'),
  working_hours: z.string().min(1, 'Need working hours'),
  weekly_holidays: z
    .number()
    .int()
    .min(0, 'Weekly holidays must be a non-negative integer'),
  professional_associations: z.array(z.string()).optional(),
  pan_number: z.string().min(1, 'PAN number is required.'),
  gst_number: z.string().min(1, 'GST number is required.'),
  // revenue_currency: z.string().min(1, 'Revenue currency is required.'),
  revenue: z.string().min(1, 'Revenue is required.'),
  // annual_turnover_currency: z
  //   .string()
  //   .min(1, 'Annual turnover currency is required.'),
  annual_turnover: z.string().min(1, 'Annual turnover is required.'),
  udyam_number: z.string().min(1, 'Udyam number is required.'),
  import_export_code: z.string().min(1, 'Import/Export code is required.'),
  factory_license_number: z
    .string()
    .min(1, 'Factory license number is required.'),
  incoterms: z.array(z.string()).optional(),
});

const CompanyDetailsScreen = ({ setSectionId }) => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiClient = createApiClient(navigate, dispatch);

  const reduxData = useSelector(
    (state) => state?.authReducer?.registrationData,
  );
  const [error, setError] = useState(null); // State for error messages
  const [isSubmitting, setIsSubmitting] = useState(false); // State for submission status
  const token = useSelector((state) => state.authReducer.token);

  const [softwaresUsed, setSoftwaresUsed] = useState(
    Array.isArray(reduxData?.software_used)
      ? reduxData.software_used
      : reduxData?.software_used?.split(',') || [],
  );
  const [serviceOffered, setServicesOffered] = useState(
    Array.isArray(reduxData?.service_offered)
      ? reduxData.service_offered
      : reduxData?.service_offered?.split(',') || [],
  );
  const [professionalAssociations, setProfessionalAssociations] = useState(
    Array.isArray(reduxData?.professional_associations)
      ? reduxData.professional_associations
      : reduxData?.professional_associations?.split(',') || [],
  );
  const [incoterms, setIncoterms] = useState(
    Array.isArray(reduxData?.incoterms)
      ? reduxData.incoterms
      : reduxData?.incoterms?.split(',') || [],
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type_of_organisation: reduxData?.type_of_organisation || '',
      establishment_year: reduxData?.establishment_year || '',
      nature_of_business: reduxData?.nature_of_business || '',
      software_used: reduxData?.software_used ? softwaresUsed : [],
      service_offered: reduxData?.service_offered ? serviceOffered : [],
      no_of_employee: reduxData?.no_of_employee
        ? Number(reduxData.no_of_employee)
        : '',
      working_hours: reduxData?.working_hours || '',
      weekly_holidays: reduxData?.weekly_holidays || '',
      professional_associations: reduxData?.professional_associations
        ? professionalAssociations
        : [],
      pan_number: reduxData?.pan_number || '',
      gst_number: reduxData?.gst_number || '',
      revenue: reduxData?.revenue || '',
      annual_turnover: reduxData?.annual_turnover || '',
      udyam_number: reduxData?.udyam_number || '',
      import_export_code: reduxData?.import_export_code || '',
      factory_license_number: reduxData?.factory_license_number || '',
      incoterms: reduxData?.incoterms ? incoterms : [],
    },
  });

  // document upload
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const [documentStates, setDocumentStates] = useState({
    pan_number_proof: null,
    gst_number_proof: null,
    udyam_number_proof: null,
    import_export_proof: null,
    factory_license_proof: null,
  });

  const [uploadProgress, setUploadProgress] = useState({
    pan_number_proof: 0,
    gst_number_proof: 0,
    udyam_number_proof: 0,
    import_export_proof: 0,
    factory_license_proof: 0,
  });

  useEffect(() => {
    fetchExistingDocuments(apiClient, setExistingDocuments, setError);
  }, [token]);

  const handleTagInput = (e, setter, fieldName) => {
    if (e.key === 'Enter' && e.target.value.trim() !== '') {
      e.preventDefault();
      const value = e.target.value.trim();
      setter((prev) => [...new Set([...prev, value])]);
      e.target.value = '';
    }
  };

  const removeTag = (index, setter) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSelect = (value) => {
    if (!incoterms.includes(value)) {
      setIncoterms((prev) => [...prev, value]);
    }
  };

  const serviceOfferedInputRef = useRef(null);
  const softwaresUsedInputRef = useRef(null);
  const professionalAssociationsInputRef = useRef(null);
  const incotermsInputRef = useRef(null);

  const onSubmit = async (data) => {
    setError(null);
    setIsSubmitting(true);

    try {
      // Prepare data for API
      const apiData = {
        type_of_organisation: data.type_of_organisation,
        establishment_year: data.establishment_year,
        nature_of_business: data.nature_of_business,
        service_offered: serviceOffered,
        software_used: softwaresUsed,
        no_of_employee: data.no_of_employee,
        working_hours: data.working_hours,
        weekly_holidays: data.weekly_holidays,
        professional_associations: professionalAssociations,
        gst_number: data.gst_number,
        pan_number: data.pan_number,
        revenue: data.revenue,
        annual_turnover: data.annual_turnover,
        udyam_number: data.udyam_number,
        importer_exporter_code: data.import_export_code,
        factory_license_number: data.factory_license_number,
        incoterms: incoterms,
        company_document_ids: [],
      };

      // Make API call
      const response = await apiClient.put(
        '/api/manufacturer-profile/company-details',
        JSON.stringify(apiData),
      );

      const responseData = response.data;
      console.log('API response:', responseData);

      const submissionData = {
        ...data,
        service_offered: serviceOffered,
        software_used: softwaresUsed,
        professional_associations: professionalAssociations,
        incoterms: incoterms,
      };

      dispatch(registerSupplierAction(submissionData));

      setSectionId(3);
    } catch (error) {
      const error_message = error?.response?.data?.error_message;
      console.error('Error: ', error_message);
      setError(
        error_message ||
          'A network error occurred. Please check your connection and try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full bg-white  px-20  rounded-md font-roboto text-gray-700">
      <h2 className="text-3xl font-semibold text-black text-center mb-4">
        Company Details
      </h2>
      <p className="text-center text-[#637381] mb-8 text-base">
        Please add specific information to impress our buyers with your capacity
        and capabilities
      </p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex gap-3">
            <FormField
              control={form.control}
              name="type_of_organisation"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Type of Organization</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger
                        className={`focus:ring-0 focus:ring-offset-0  focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple  ${field.value ? 'text-black' : 'text-gray-500'} `}
                      >
                        <SelectValue placeholder="Select from below" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Private">Proprietary</SelectItem>
                      <SelectItem value="Private">Private</SelectItem>
                      <SelectItem value="Public">Public</SelectItem>
                      <SelectItem value="NGO">NGO</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="establishment_year"
              render={({ field }) => (
                <FormItem className="flex-3">
                  <FormLabel>Establishment Year</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. 2000"
                      className=" text-black focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple "
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="nature_of_business"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nature of Business</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger
                      className={`focus:ring-0 focus:ring-offset-0  focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple  ${field.value ? 'text-black' : 'text-gray-500'} `}
                    >
                      <SelectValue placeholder="Select from below" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {/* Manufacturer / Sole selling agent / Dealer / Trader/reseller /other) */}
                    <SelectItem value="Manufacturer">Manufacturer</SelectItem>
                    <SelectItem value="Sole selling agent">
                      Sole selling agent
                    </SelectItem>
                    <SelectItem value="Dealer">Dealer</SelectItem>
                    <SelectItem value="Trader/reseller">
                      Trader/reseller
                    </SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* Language Supported */}
          {/* <FormField
            control={form.control}
            name="languages_supported"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  Languages Supported{' '}
                  <span className="text-gray-400 ">( Press enter to add )</span>
                </FormLabel>
                <FormControl>
                  <div className="w-full">
                    <div className="relative">
                      <Input
                        placeholder="Enter the languages supported by the company"
                        onKeyDown={(e) =>
                          handleTagInput(
                            e,
                            setLanguagesSupported,
                            'languages_supported',
                          )
                        }
                        ref={serviceOfferedInputRef}
                        className="focus:ring-0 focus:ring-offset-0 text-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = serviceOfferedInputRef.current;
                          if (input && input.value.trim() !== '') {
                            handleTagInput(
                              {
                                key: 'Enter',
                                target: input,
                                preventDefault() {},
                              },
                              setLanguagesSupported,
                              'languages_supported',
                            );
                          }
                        }}
                        className="absolute inset-y-0 right-2 flex items-center justify-center text-lightPurple hover:text-darkerPurple transition"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-2">
                      {languagesSupported.map((service, index) => (
                        <div
                          key={service}
                          className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center"
                        >
                          {service}
                          <button
                            type="button"
                            onClick={() =>
                              removeTag(
                                index,
                                setLanguagesSupported,
                                'languages_supported',
                              )
                            }
                            className="ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          /> */}

          {/* Services Offered */}
          <FormField
            control={form.control}
            name="service_offered"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  Services Offered{' '}
                  <span className="text-gray-400 ">( Press enter to add )</span>
                </FormLabel>
                <FormControl>
                  <div className="w-full">
                    <div className="relative">
                      <Input
                        placeholder="Enter the services offered by the company"
                        onKeyDown={(e) =>
                          handleTagInput(
                            e,
                            setServicesOffered,
                            'service_offered',
                          )
                        }
                        ref={serviceOfferedInputRef}
                        className="focus:ring-0 focus:ring-offset-0 text-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = serviceOfferedInputRef.current;
                          if (input && input.value.trim() !== '') {
                            handleTagInput(
                              {
                                key: 'Enter',
                                target: input,
                                preventDefault() {},
                              },
                              setServicesOffered,
                              'service_offered',
                            );
                          }
                        }}
                        className="absolute inset-y-0 right-2 flex items-center justify-center text-lightPurple hover:text-darkerPurple transition"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-2">
                      {serviceOffered.map((service, index) => (
                        <div
                          key={service}
                          className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center"
                        >
                          {service}
                          <button
                            type="button"
                            onClick={() =>
                              removeTag(
                                index,
                                setServicesOffered,
                                'service_offered',
                              )
                            }
                            className="ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Software Used by the company */}
          <FormField
            control={form.control}
            name="software_used"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  Software Used{' '}
                  <span className="text-gray-400 ">( Press enter to add )</span>
                </FormLabel>
                <FormControl>
                  <div>
                    <div className="relative">
                      <Input
                        placeholder="Enter the software used by the company"
                        onKeyDown={(e) =>
                          handleTagInput(e, setSoftwaresUsed, 'software_used')
                        }
                        ref={softwaresUsedInputRef}
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = softwaresUsedInputRef.current;
                          if (input && input.value.trim() !== '') {
                            handleTagInput(
                              {
                                key: 'Enter',
                                target: input,
                                preventDefault() {},
                              },
                              setSoftwaresUsed,
                              'software_used',
                            );
                          }
                        }}
                        className="absolute inset-y-0 right-2 flex items-center justify-center text-lightPurple hover:text-darkerPurple transition"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {softwaresUsed.map((software, index) => (
                        <div
                          key={software}
                          className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center"
                        >
                          {software}
                          <button
                            type="button"
                            onClick={() =>
                              removeTag(
                                index,
                                setSoftwaresUsed,
                                'software_used',
                              )
                            }
                            className="ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="no_of_employee"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Number of Employees</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    placeholder="Enter number of employees"
                    {...field}
                    // Only allow number inputs

                    onChange={(e) => {
                      const value =
                        e.target.value === '' ? '' : Number(e.target.value);
                      field.onChange(value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex gap-4">
            <FormField
              control={form.control}
              name="working_hours"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Working Hours</FormLabel>
                  <FormControl>
                    <Input
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      placeholder="e.g. 9 AM to 6 PM"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="weekly_holidays"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Weekly Holidays
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. 2"
                      type="number"
                      {...field}
                      min="0"
                      max="6"
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === '' ? '' : Number(e.target.value),
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Professional Associations */}
          <FormField
            control={form.control}
            name="professional_associations"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  Professional Associations / Acreditions{' '}
                  <span className="text-gray-400 ">( Press enter to add )</span>
                </FormLabel>
                <FormControl>
                  <div>
                    <div className="relative">
                      <Input
                        placeholder="Mention the association you are part of"
                        onKeyDown={(e) =>
                          handleTagInput(
                            e,
                            setProfessionalAssociations,
                            'professional_associations',
                          )
                        }
                        ref={professionalAssociationsInputRef}
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input =
                            professionalAssociationsInputRef.current;
                          if (input && input.value.trim() !== '') {
                            handleTagInput(
                              {
                                key: 'Enter',
                                target: input,
                                preventDefault() {},
                              },
                              setProfessionalAssociations,
                              'professional_associations',
                            );
                          }
                        }}
                        className="absolute inset-y-0 right-2 flex items-center justify-center text-lightPurple hover:text-darkerPurple transition"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {professionalAssociations.map((association, index) => (
                        <div
                          key={association}
                          className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center"
                        >
                          {association}
                          <button
                            type="button"
                            onClick={() =>
                              removeTag(
                                index,
                                setProfessionalAssociations,
                                'professional_associations',
                              )
                            }
                            className="ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="pan_number"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>PAN Number</FormLabel>
                <FormControl>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter your 12 digit PAN number"
                      {...field}
                      className="focus:ring-0 w-4/5 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-black focus-visible:outline-none focus:outline-none focus:border-lightPurple "
                    />
                    <FileUpload
                      DOCUMENT_TITLES={DOCUMENT_TITLES}
                      MAX_FILE_SIZE={MAX_FILE_SIZE}
                      ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                      existingDocuments={existingDocuments}
                      setExistingDocuments={setExistingDocuments}
                      documentStates={documentStates}
                      setDocumentStates={setDocumentStates}
                      uploadProgress={uploadProgress}
                      setUploadProgress={setUploadProgress}
                      isUploading={isUploading}
                      setIsUploading={setIsUploading}
                      setError={setError}
                      id="pan_number_proof"
                      key="pan_number_proof"
                      label="Attach file"
                      documentType="pan_number_proof"
                      existingDocument={existingDocuments.find(
                        (doc) => doc.document_name === 'pan_number_proof',
                      )}
                      classNames="w-36 h-10"
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="gst_number"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>GST Number</FormLabel>
                <FormControl>
                  <div className="flex gap-2">
                    <Input
                      placeholder="27AAAPA1234A1Z5"
                      {...field}
                      className="focus:ring-0 w-4/5 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-black focus-visible:outline-none focus:outline-none focus:border-lightPurple "
                    />
                    <FileUpload
                      DOCUMENT_TITLES={DOCUMENT_TITLES}
                      MAX_FILE_SIZE={MAX_FILE_SIZE}
                      ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                      existingDocuments={existingDocuments}
                      setExistingDocuments={setExistingDocuments}
                      documentStates={documentStates}
                      setDocumentStates={setDocumentStates}
                      uploadProgress={uploadProgress}
                      setUploadProgress={setUploadProgress}
                      isUploading={isUploading}
                      setIsUploading={setIsUploading}
                      setError={setError}
                      id="gst_number_proof"
                      key="gst_number_proof"
                      label="Attach file"
                      documentType="gst_number_proof"
                      existingDocument={existingDocuments.find(
                        (doc) => doc.document_name === 'gst_number_proof',
                      )}
                      classNames="w-36 h-10"
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Revenue */}
          <FormField
            control={form.control}
            name="revenue"
            render={({ field }) => (
              <FormItem className="flex-3">
                <FormLabel>Revenue</FormLabel>
                <FormControl>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center p-2 border border-gray-200 rounded-md">
                      <span className="text-gray-500 mr-2 border-r pr-2">
                        US
                      </span>
                      <span className="text-gray-500 mr-2">$</span>
                    </div>
                    <Input
                      placeholder="e.g. 2,00,000"
                      className=" text-black focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple "
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Annual TurnOver */}
          <FormField
            control={form.control}
            name="annual_turnover"
            render={({ field }) => (
              <FormItem className="flex-3">
                <FormLabel>Annual Turnover</FormLabel>
                <FormControl>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center p-2 border border-gray-200 rounded-md">
                      <span className="text-gray-500 mr-2 border-r pr-2">
                        US
                      </span>
                      <span className="text-gray-500 mr-2">$</span>
                    </div>
                    <Input
                      placeholder="e.g. 2,00,00,000"
                      className=" text-black focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple "
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="udyam_number"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>Udyam Number</FormLabel>
                <FormControl>
                  <div className="flex gap-2">
                    <Input
                      placeholder="UDYAM-XX-00-0000000"
                      {...field}
                      className="focus:ring-0 w-4/5 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-black focus-visible:outline-none focus:outline-none focus:border-lightPurple "
                    />
                    <FileUpload
                      DOCUMENT_TITLES={DOCUMENT_TITLES}
                      MAX_FILE_SIZE={MAX_FILE_SIZE}
                      ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                      existingDocuments={existingDocuments}
                      setExistingDocuments={setExistingDocuments}
                      documentStates={documentStates}
                      setDocumentStates={setDocumentStates}
                      uploadProgress={uploadProgress}
                      setUploadProgress={setUploadProgress}
                      isUploading={isUploading}
                      setIsUploading={setIsUploading}
                      setError={setError}
                      id="udyam_number_proof"
                      key="udyam_number_proof"
                      label="Attach file"
                      documentType="udyam_number_proof"
                      existingDocument={existingDocuments.find(
                        (doc) => doc.document_name === 'udyam_number_proof',
                      )}
                      classNames="w-36 h-10"
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="import_export_code"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>Importer Exporter Code</FormLabel>
                <FormControl>
                  <div className="flex gap-2">
                    <Input
                      placeholder="AAA-12345678-BB"
                      {...field}
                      className="focus:ring-0 w-4/5 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-black focus-visible:outline-none focus:outline-none focus:border-lightPurple "
                    />
                    <FileUpload
                      DOCUMENT_TITLES={DOCUMENT_TITLES}
                      MAX_FILE_SIZE={MAX_FILE_SIZE}
                      ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                      existingDocuments={existingDocuments}
                      setExistingDocuments={setExistingDocuments}
                      documentStates={documentStates}
                      setDocumentStates={setDocumentStates}
                      uploadProgress={uploadProgress}
                      setUploadProgress={setUploadProgress}
                      isUploading={isUploading}
                      setIsUploading={setIsUploading}
                      setError={setError}
                      id="import_export_proof"
                      key="import_export_proof"
                      label="Attach file"
                      documentType="import_export_proof"
                      existingDocument={existingDocuments.find(
                        (doc) => doc.document_name === 'import_export_proof',
                      )}
                      classNames="w-36 h-10"
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="factory_license_number"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>Factory License Number</FormLabel>
                <FormControl>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter your factory license number"
                      {...field}
                      className="focus:ring-0 w-4/5 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-black focus-visible:outline-none focus:outline-none focus:border-lightPurple "
                    />
                    <FileUpload
                      DOCUMENT_TITLES={DOCUMENT_TITLES}
                      MAX_FILE_SIZE={MAX_FILE_SIZE}
                      ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                      existingDocuments={existingDocuments}
                      setExistingDocuments={setExistingDocuments}
                      documentStates={documentStates}
                      setDocumentStates={setDocumentStates}
                      uploadProgress={uploadProgress}
                      setUploadProgress={setUploadProgress}
                      isUploading={isUploading}
                      setIsUploading={setIsUploading}
                      setError={setError}
                      id="factory_license_proof"
                      key="factory_license_proof"
                      label="Attach file"
                      documentType="factory_license_proof"
                      existingDocument={existingDocuments.find(
                        (doc) => doc.document_name === 'factory_license_proof',
                      )}
                      classNames="w-36 h-10"
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Incoterms */}
          <FormField
            control={form.control}
            name="incoterms"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  Incoterms{' '}
                  <span className="text-gray-400">( Select multiple )</span>
                </FormLabel>
                <FormControl>
                  <div>
                    <Select onValueChange={handleSelect}>
                      <FormControl>
                        <SelectTrigger
                          className={`focus:ring-0 focus:ring-offset-0  focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple  ${field.value ? 'text-black' : 'text-gray-500'} `}
                        >
                          <SelectValue placeholder="Select Incoterm" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {allIncotermOptions
                          .filter((option) => !incoterms.includes(option))
                          .map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>

                    {/* Selected Tags */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {incoterms.map((item, index) => (
                        <div
                          key={item}
                          className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center"
                        >
                          {item}
                          <button
                            type="button"
                            onClick={() =>
                              removeTag(index, setIncoterms, 'incoterms`')
                            }
                            className="ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-between mt-8 text-lg">
            <Button
              type="button"
              variant="outline"
              onClick={() => setSectionId(1)}
              className="px-4 py-2 h-12 w-36 hover:bg-slate-200"
            >
              Back
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 h-12 w-36 bg-darkPurple hover:bg-darkerPurple ease-in duration-300"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span className="font-roboto font-semibold text-base">
                    Saving...
                  </span>
                </>
              ) : (
                <span className="font-roboto font-semibold text-base">
                  Next
                </span>
              )}
            </Button>
          </div>
          <div className="flex flex-row items-center justify-center space-x-2 ">
            {/* conditional renderign if no error display this if error dispaly error instead */}
            {error ? (
              <>
                <ShieldAlert className="h-3 w-3" />
                <span className="text-xs text-red-600">{error}</span>
              </>
            ) : (
              <>
                <LockClosedIcon className="h-3 w-3" />
                <span className="text-xs text-[#2CD673]">
                  We use industry-standard encryption to protect your data
                </span>
              </>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
};

export default CompanyDetailsScreen;
